"use client";

import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { api } from "@/lib/api";
import { isTokenExpired } from "@/lib/socket-auth";

// Paridade fork (BaseActionCableConnector.js:3): heartbeat de presença a 20s.
const PRESENCE_INTERVAL = 20_000;

/**
 * Listeners registrados por OUTROS módulos (hoje: o sino de notificações) que
 * precisam de eventos da sala `user:{id}` sem abrir conexão própria — o socket
 * de presença já está montado no layout do dashboard e o gateway já o coloca
 * nessa sala (chat.gateway.ts:432).
 *
 * ⚠️ Este mecanismo NÃO altera o ciclo de vida da conexão: não mexe nas deps do
 * useEffect, em `reconnection`, nem chama connect/disconnect. Só adiciona e
 * remove handlers. Se aparecer reconexão anormal ou agente sumindo da fila de
 * distribuição, este é o primeiro lugar a olhar — e o rollback é remover apenas
 * `onPresenceEvent`, sem tocar no resto do arquivo.
 */
const listenersExternos = new Map<string, Set<(data: unknown) => void>>();
let socketAtivo: Socket | null = null;

/**
 * Registra um listener num evento do socket de presença; devolve o cleanup.
 *
 * Se o socket ainda não subiu (ordem de montagem dos providers não é garantida),
 * o listener fica no Map e é aplicado assim que a conexão nascer — por isso o
 * Map vive fora do ciclo do hook.
 */
export function onPresenceEvent(
  evento: string,
  cb: (data: unknown) => void,
): () => void {
  if (!listenersExternos.has(evento)) listenersExternos.set(evento, new Set());
  listenersExternos.get(evento)!.add(cb);
  socketAtivo?.on(evento, cb as (...args: unknown[]) => void);

  return () => {
    listenersExternos.get(evento)?.delete(cb);
    socketAtivo?.off(evento, cb as (...args: unknown[]) => void);
  };
}

/**
 * Conexão de presença leve, montada no layout do dashboard. Mantém o agente
 * "online" no fork enquanto logado na Platform — em QUALQUER tela, não só na
 * de conversas. Conecta ao /chat em modo presence-only (auth.presence), que no
 * gateway pula os rooms de conversa mas dispara onConnect/onDisconnect
 * (presença). Crítico pra fila de distribuição — ver spec
 * 2026-05-25-presenca-global-agente-design.md.
 */
export function usePresenceConnection() {
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshAttemptedRef = useRef(false);

  useEffect(() => {
    const token = api.getAccessToken();
    if (!token) return;

    const apiUrl =
      process.env.NEXT_PUBLIC_API_URL ||
      (typeof window !== "undefined"
        ? `${window.location.origin}/api`
        : "/api");
    const wsUrl = apiUrl.replace("/api", "");

    const socket: Socket = io(`${wsUrl}/chat`, {
      auth: { token, presence: true },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
    });

    // Publica o socket pros listeners externos e reaplica os que se
    // registraram antes de ele existir. Não altera nada da conexão em si.
    socketAtivo = socket;
    for (const [evento, cbs] of listenersExternos) {
      for (const cb of cbs) socket.on(evento, cb as (...args: unknown[]) => void);
    }

    const stopHeartbeat = () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
    const startHeartbeat = () => {
      stopHeartbeat();
      heartbeatRef.current = setInterval(() => {
        if (socket.connected) socket.emit("update_presence");
      }, PRESENCE_INTERVAL);
    };

    socket.on("connect", () => {
      refreshAttemptedRef.current = false;
      startHeartbeat();
    });
    socket.on("disconnect", () => {
      stopHeartbeat();
    });

    // Token expirado: refresh + reconnect com token novo. Espelha
    // use-chat-socket.ts doRefreshAndReconnect. Sem isso o socket de presença
    // cairia a cada ~15min (expiração do access token) e o agente sumiria da
    // fila — o pior bug possível aqui.
    const doRefreshAndReconnect = () => {
      if (refreshAttemptedRef.current) return;
      refreshAttemptedRef.current = true;
      api
        .refreshTokens()
        .then((ok) => {
          if (!ok) return; // refreshTokens já redireciona /login se falhar
          const newToken = api.getAccessToken();
          if (!newToken) return;
          socket.auth = { token: newToken, presence: true };
          socket.disconnect();
          socket.connect();
        })
        .catch(() => {});
    };

    socket.on("auth_required", () => doRefreshAndReconnect());
    socket.on("connect_error", () => {
      if (isTokenExpired(api.getAccessToken())) doRefreshAndReconnect();
    });

    // Reforço de offline: fecha o socket ao sair da página (além do unmount no
    // logout). Garante onDisconnect → offline imediato.
    const onPageHide = () => socket.disconnect();
    window.addEventListener("pagehide", onPageHide);

    // #148: logout dispara o fechamento imediato do socket de presença (reforço
    // client-side do teardown server-side).
    const onLogout = () => socket.disconnect();
    window.addEventListener("gk:presence:disconnect", onLogout);

    // #P2: ao voltar o foco pra aba (agente volta do ERP/outro app), reconectar
    // o socket de presença na hora se ele tiver caído — o backoff do socket.io é
    // estrangulado pelo Chrome em aba de background, então sem isto o agente pode
    // demorar minutos pra reaparecer online. Espelha o restore_presence do fork
    // (BaseActionCableConnector) e o use-chat-socket.ts.
    const onVisible = () => {
      if (document.visibilityState === "visible" && !socket.connected) {
        socket.connect();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    // #111: token rotacionado atualiza socket.auth do socket de presenca VIVO
    // (mantem presence:true), pra reconexao organica nao cair com token congelado
    // e o agente nao sumir da fila de distribuicao.
    const unsubTokenRefresh = api.onTokenRefreshed((newToken) => {
      socket.auth = { token: newToken, presence: true };
    });

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("gk:presence:disconnect", onLogout);
      document.removeEventListener("visibilitychange", onVisible);
      unsubTokenRefresh();
      stopHeartbeat();
      socketAtivo = null;
      socket.disconnect();
    };
  }, []);
}
